// ============================================================================
// PACK BUILDER HELP — Centralized contextual guidance for each builder section
// UI-only. Never written to Firestore. Does not affect pack schema or Gate engine.
// ============================================================================

const PACK_BUILDER_HELP = {
  metadata: {
    title: 'Pack Setup',
    purpose:
      'Set up the Pack’s name, description, and version before defining referral rules.',
    question:
      'What exact clinical pathway am I defining, and what name would make its purpose immediately clear to another clinician?',
    goodInput: 'Use a pathway-specific name, not a vague label.',
    example: 'Orthopedics — Knee Pain / Meniscus Concern',
    avoid: 'Generic names like “Skin 1” or “General Form.”',
    subSections: [
      {
        heading: 'Description',
        purpose:
          'Explain what this Pack evaluates, who it is for, and what kind of referral it should be used for.',
        question:
          'What does this Pack evaluate, and when should a referring provider choose it?',
        goodInput:
          'A clear 1–2 sentence explanation of the referral pathway, the main clinical concern, and the kind of criteria captured.',
        example:
          'Evaluates referrals for knee pain where meniscus injury, mechanical symptoms, swelling, or persistent functional limitation are suspected. Captures injury history, red flags, prior treatment, imaging status, and minimum clinical criteria needed for orthopedic review.',
        avoid:
          'Descriptions that are too vague, such as:\n”General knee pack”\n”Skin referral form”\n”Used for patients”',
      },
    ],
  },
  scope: {
    title: 'Referral Context & Escalation',
    purpose:
      'Every referral includes Visit Information by default. Visit Type and Visit Purpose are always collected. This section defines the escalation reasons a referring provider can select when requesting faster review.',
    question:
      'When should the referring provider be allowed to escalate this referral, and what specific reasons should be available?',
    goodInput:
      'Define specific, clinically meaningful escalation reasons.',
    example:
      'Escalation reasons:\n• Rapidly worsening symptoms\n• Failed conservative management\n• Patient safety concern\n• Severe functional decline\n• High-risk clinical concern',
    avoid:
      'Adding vague reasons like "Other," "General concern," or "Doctor request."\n\nEach reason should clearly explain why faster review or escalation may be justified.',
  },
  redirectFlags: {
    title: 'Redirect Flags',
    purpose:
      'Redirect flags identify referrals that belong to another Pack or another specialty before this Pack creates a referral form.',
    question:
      'When is this referral in the wrong place before the Gate evaluates urgency or completeness?',
    goodInput: 'Use redirects only when ownership changes.',
    example: 'Oral mucosal lesion → ENT / Oral Surgery',
    avoid: 'Do not put urgent features here. Urgency belongs in Red Flags.',
  },
  redFlags: {
    title: 'Red Flags',
    purpose:
      'Red flags identify findings that force an urgent outcome inside the correct pathway. They are evaluated after redirect checks.',
    question:
      'What findings mean this is still the right pathway, but needs faster review?',
    goodInput: 'Use red flags for danger signs, high-risk findings, or fast-review triggers.',
    example: 'Rapidly changing lesion.\nBleeding or ulcerated lesion.',
    avoid: 'Do not use red flags to redirect to another specialty. Redirects belong in Redirect Flags.',
  },
  fields: {
    title: 'Clinical Fields',
    purpose:
      'Clinical fields define the structured facts the referring provider must provide. Missing required clinical fields can produce a Not-Yet outcome.',
    question:
      'What clinical facts must the referring provider give us before this referral can be considered complete enough for routine review?',
    goodInput: 'Use structured fields whenever possible. Break the clinical story into specific facts the Gate can evaluate.',
    example:
      'Instead of:\n"Describe the knee problem"\n\nUse:\nField: Main symptom\nType: Select\nOptions: Pain, locking, swelling, instability, reduced range of motion\n\nField: Symptom duration\nType: Select\nOptions: Less than 2 weeks, 2–6 weeks, 6 weeks–3 months, more than 3 months\n\nField: Pain severity\nType: Slider\nRange: 0–10\n\nField: Prior treatment tried\nType: Multiselect\nOptions: Rest, physiotherapy, NSAIDs, injection, none',
    notYetGuidance:
      'When you mark a clinical field as "Required for Routine outcome," the referring provider must answer it for the referral to be complete enough for routine review.\n\nIf the field is missing, the Gate can produce a Not-Yet outcome.\n\nUse "Not-Yet instruction if missing" to tell the referring provider exactly what to provide.\n\nExample:\nField: Lesion duration\nNot-Yet instruction: Provide approximate lesion duration before routine dermatology review.\n\nThe Not-Yet Recovery tab shows all generated recovery instructions for review. You do not need to enter them again there.',
    avoid: 'Using one large textarea for all clinical information.\n\nIf the answer can be selected, checked, numbered, dated, or scaled, do not make it free text.',
  },
  attachments: {
    title: 'Attachment Requirements',
    purpose:
      'Attachments define documents, photos, reports, or files intake expects before scheduling.',
    question:
      'What documents, photos, reports, or files does intake need before this referral can move safely toward scheduling?',
    goodInput:
      'Use clear labels and specific attachment types. Mark only the files intake truly needs before scheduling as required.',
    example:
      '• Clinical photo of lesion\n  Type: Clinical Photo\n  Required for intake completeness: Yes\n\n• MRI report\n  Type: Imaging Report\n  Required for intake completeness: Yes\n\n• Prior specialist note\n  Type: Referral Note\n  Required for intake completeness: No',
    avoid:
      'Treating attachments as Not-Yet clinical criteria unless the Gate engine explicitly evaluates them.',
    important:
      'Missing required attachments create Needs Info / rework. They do not change the Gate outcome.',
  },
  notYetSteps: {
    title: 'Not-Yet Recovery',
    purpose:
      'Not-Yet recovery instructions are auto-generated from required clinical fields. They tell the referring provider exactly what to provide when required clinical criteria are missing.',
    question:
      'If this referral is incomplete, what exact action should the referring provider take to make it complete?',
    goodInput: 'Recovery instructions are generated automatically when you mark a clinical field as required and provide a Not-Yet instruction. You can refine the wording here.',
    example:
      '• Missing field: Lesion duration\n  Step: Provide approximate lesion duration before routine dermatology review.\n\n• Missing field: Prior treatment tried\n  Step: Document what treatment has already been tried, or choose “None tried.”',
    avoid: 'Vague messages like “More information needed.”\n\nDo not use Not-Yet steps for missing attachments unless the Gate engine explicitly evaluates that attachment as clinical criteria.',
  },
  patientMessages: {
    title: 'Outcome Messages',
    purpose:
      'Defines plain-language messages shown for each Gate outcome.',
    question:
      'What should the patient/referring side understand about the outcome without being confused, alarmed, or overloaded?',
    goodInput: 'Keep messages clear, short, and non-alarming.',
    example:
      'Your referral has the required clinical information for routine review.',
    avoid: 'Overly technical, frightening, or diagnosis-suggesting language.',
    important:
      'These messages should explain the workflow outcome, not make clinical promises. Do not include PHI. Do not include patient-specific details. Do not use overly alarming language. Do not imply a diagnosis.',
  },
  review: {
    title: 'Review & Publish',
    purpose:
      'Final review before the Pack becomes active and available in the Gate.',
    question:
      'Does this Pack create clear Form from chaos — correct routing, clear urgency, complete criteria, required intake items, and a documented next step?',
    goodInput:
      'Check that clinical fields, red flags, redirects, attachments, Not-Yet recovery instructions, and outcome messages all match the intended workflow.',
    avoid:
      'Publishing a Pack with vague scope, missing recovery instructions, unresolved redirect targets, or unclear required fields.',
  },
};

export default PACK_BUILDER_HELP;
